import * as Yup from 'yup';
import { Autocomplete, Button, FormSelectField, FormTextField } from 'ui';
import { Form, Formik } from 'formik';
import { JceVenueCollectionEntryEntity } from '../../generated/graphql';
import {
  JceVenueCollectionIconIndicator,
  JceVenueCollectionStatus,
  useCreateJceVenueCollectionMutation,
  useUpdateJceVenueCollectionMutation,
} from 'generated/graphql';
import { getCollectionsForGeographyRefetch } from '../../components/jceGeographyDetail/queries';
import { gql } from '@apollo/client';
import { useLocation, useNavigate, useParams } from 'react-router-v7';
import ClowncarBodyV2 from 'components/ClowncarBodyV2';
import ClowncarHeaderV2 from 'components/ClowncarHeaderV2';
import React from 'react';
import VenueCollectionDetailForm from '../../components/jceGeographyDetail/VenueCollectionDetailForm';

gql`
  mutation CreateJceVenueCollection($collection: JceVenueCollectionInput!, $geographyId: Int!) {
    createJceVenueCollection(collection: $collection, geographyId: $geographyId) {
      id
      name
      shortName
      description
      iconIndicator
      status
      urlSlug
    }
  }
`;

gql`
  mutation UpdateJceVenueCollection($collection: JceVenueCollectionInput!) {
    updateJceVenueCollection(collection: $collection) {
      id
      name
      shortName
      description
      iconIndicator
      status
      urlSlug
    }
  }
`;

const STATUS_OPTIONS = Object.values(JceVenueCollectionStatus) as JceVenueCollectionStatus[];
const ICON_OPTIONS = Object.values(JceVenueCollectionIconIndicator).filter(
  (icon) => icon !== JceVenueCollectionIconIndicator.IconUnknown
) as JceVenueCollectionIconIndicator[];

const DEFAULT_STATUS = JceVenueCollectionStatus.Draft;
const DEFAULT_ICON = JceVenueCollectionIconIndicator.IconDineIn;

/** Converts e.g. "ICON_DINE_IN" → "Dine In" */
function formatIconLabel(icon: JceVenueCollectionIconIndicator): string {
  return icon
    .replace(/^ICON_/, '')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

const ICON_LABELS = new Map(ICON_OPTIONS.map((icon) => [icon, formatIconLabel(icon)]));

/** Converts e.g. "ACTIVE" → "Active" */
function formatStatusLabel(status: JceVenueCollectionStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

export const STATUS_LABELS = new Map(STATUS_OPTIONS.map((s) => [s, formatStatusLabel(s)]));

export interface JceVenueCollectionFormValues {
  name: string;
  shortName: string;
  description: string;
  iconIndicator: string;
  status: string;
  urlSlug: string;
  collectionEntrys: JceVenueCollectionEntryEntity[];
}

const collectionSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  shortName: Yup.string().required('Short name is required'),
  description: Yup.string().required('Description is required'),
  iconIndicator: Yup.string().required('Icon indicator is required'),
  status: Yup.string().required('Status is required'),
  urlSlug: Yup.string().required('Url slug is required'),
});

const emptyState: JceVenueCollectionFormValues = {
  name: '',
  shortName: '',
  description: '',
  iconIndicator: DEFAULT_ICON,
  status: DEFAULT_STATUS,
  urlSlug: '',
  collectionEntrys: [],
};

const JceVenueCollectionFormRoute: React.FC = () => {
  const { geographyId = '-1', collectionId } = useParams<{
    geographyId: string;
    collectionId?: string;
  }>();
  const isEditing = !!collectionId;
  const navigate = useNavigate();
  const location = useLocation();

  const [createCollection] = useCreateJceVenueCollectionMutation();
  const [updateCollection] = useUpdateJceVenueCollectionMutation();

  // Initial values come from router location state when editing (passed by the detail page)
  const initialValues =
    isEditing && location.state ? (location.state as JceVenueCollectionFormValues) : emptyState;

  const refetchSpecifier = getCollectionsForGeographyRefetch(Number(geographyId));

  const onSubmit = async (values: JceVenueCollectionFormValues) => {
    if (isEditing) {
      await updateCollection({
        variables: {
          collection: {
            id: Number(collectionId),
            name: values.name,
            shortName: values.shortName || null,
            description: values.description,
            iconIndicator: values.iconIndicator as JceVenueCollectionIconIndicator,
            status: values.status as JceVenueCollectionStatus,
            urlSlug: values.urlSlug,
          },
        },
        refetchQueries: refetchSpecifier,
      });
    } else {
      await createCollection({
        variables: {
          collection: {
            name: values.name,
            shortName: values.shortName || null,
            description: values.description,
            iconIndicator: values.iconIndicator as JceVenueCollectionIconIndicator,
            status: values.status as JceVenueCollectionStatus,
            urlSlug: values.urlSlug,
          },
          geographyId: Number(geographyId),
        },
        refetchQueries: refetchSpecifier,
      });
    }
    navigate(-1);
  };

  return (
    <div className="Content">
      <ClowncarHeaderV2
        heading={isEditing ? `Edit Collection #${collectionId}` : 'Create Collection'}
        actions={
          <Button variant="outlined" onClick={() => navigate(-1)}>
            ← Back
          </Button>
        }
      />
      <ClowncarBodyV2>
        <Formik<JceVenueCollectionFormValues>
          initialValues={initialValues}
          enableReinitialize
          validationSchema={collectionSchema}
          onSubmit={onSubmit}
        >
          {({ values, dirty, isValid, isSubmitting, setFieldValue }) => (
            <Form style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                <FormTextField fullWidth label="Name" name="name" value={values.name} required />
                <FormTextField
                  fullWidth
                  label="Short Name"
                  name="shortName"
                  value={values.shortName}
                  required
                />
                <FormTextField
                  fullWidth
                  label="URL Slug"
                  name="urlSlug"
                  value={values.urlSlug}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <FormTextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={values.description}
                  multiline
                  rows={3}
                  required
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 200 }}>
                  <Autocomplete<JceVenueCollectionIconIndicator>
                    label="Icon Indicator"
                    options={ICON_OPTIONS}
                    value={values.iconIndicator as JceVenueCollectionIconIndicator}
                    onChange={(_e, value) => setFieldValue('iconIndicator', value ?? DEFAULT_ICON)}
                    getOptionLabel={(icon) => ICON_LABELS.get(icon) ?? icon}
                    defaultPopper
                  />
                  <FormSelectField label="Status" name="status" value={values.status}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABELS.get(s) ?? s}
                      </option>
                    ))}
                  </FormSelectField>
                </div>
              </div>
              <div>
                <Button
                  color="primary"
                  disabled={!dirty || !isValid || isSubmitting}
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  {isEditing ? 'Save Changes' : 'Create'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
        <VenueCollectionDetailForm
          geographyId={geographyId}
          collectionId={collectionId}
          collectionEntrys={initialValues.collectionEntrys ?? []}
        />
      </ClowncarBodyV2>
    </div>
  );
};

export default JceVenueCollectionFormRoute;
